import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthenticateWithRedirectCallback } from '@clerk/react';
import { SignInPage } from '../pages/SignInPage';
import { SignUpPage } from '../pages/SignUpPage';
import { UnauthorizedPage } from '../pages/UnauthorizedPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { ProtectedRoute } from './ProtectedRoute';
import { Dashboard } from '../pages/Dashboard';
import { LandingPage } from '../pages/LandingPage';
import { PricingPage } from '../pages/PricingPage';
import { UseCasesPage } from '../pages/UseCasesPage';
import { UseCaseDetailPage } from '../pages/UseCaseDetailPage';
import { AboutPage } from '../pages/AboutPage';
import { MissionPage } from '../pages/MissionPage';
import { NewsroomPage } from '../pages/NewsroomPage';
import { ContactPage } from '../pages/ContactPage';
import { PrivacyPage } from '../pages/PrivacyPage';
import { TermsPage } from '../pages/TermsPage';
import { CookiePage } from '../pages/CookiePage';
import { SecurityPage } from '../pages/SecurityPage';
import { DpaPage } from '../pages/DpaPage';
import { AupPage } from '../pages/AupPage';
import { DmcaPage } from '../pages/DmcaPage';
import { GlobalPortal } from '../pages/GlobalPortal';
import { SystemAdminPage } from '../pages/SystemAdminPage';
import { TemplatesPage } from '../pages/system-admin/TemplatesPage';
import { AuditPage } from '../pages/system-admin/AuditPage';
import { TicketsPage } from '../pages/system-admin/TicketsPage';
import { NewsroomManagerPage } from '../pages/system-admin/NewsroomManagerPage';
import { PulsePage } from '../pages/PulsePage';
import { PublicPulsePage } from '../pages/PublicPulsePage';
import { ChatPage } from '../pages/ChatPage';
import { CalendarPage } from '../pages/CalendarPage';
import { LedgerPage } from '../pages/LedgerPage';
import { StripeMockDashboardPage } from '../pages/StripeMockDashboardPage';
import { StripeCallbackPage } from '../pages/StripeCallbackPage';
import { RequirementsPage } from '../pages/RequirementsPage';
import { GoalsPage } from '../pages/GoalsPage';
import { SettingsPage } from '../pages/SettingsPage';
import { ModuleSettingsPage } from '../pages/settings/ModuleSettingsPage';
import { BrandingPage } from '../pages/settings/BrandingPage';
import { MemberSettingsPage } from '../pages/settings/MemberSettingsPage';
import { BillingSettingsPage } from '../pages/settings/BillingSettingsPage';
import { PayoutSettingsPage } from '../pages/settings/PayoutSettingsPage';
import { DataSettingsPage } from '../pages/settings/DataSettingsPage';
import { TerminologyPage } from '../pages/settings/TerminologyPage';
import { VaultPage } from '../pages/VaultPage';
import { NewsletterPage } from '../pages/NewsletterPage';
import { DirectoryPage } from '../pages/DirectoryPage';
import { DiscussionsPage } from '../pages/DiscussionsPage';
import { SupportPage } from '../pages/SupportPage';
import { SubOrgsPage } from '../pages/SubOrgsPage';
import { NotificationSettingsPage } from '../pages/NotificationSettingsPage';
import { ReferralsPage } from '../pages/ReferralsPage';
import { LeadershipPage } from '../pages/LeadershipPage';
import { ElectionsPage } from '../pages/ElectionsPage';
import { AnalyticsPage } from '../pages/AnalyticsPage';
import { PollsPage } from '../pages/PollsPage';
import { ProfilePage } from '../pages/ProfilePage';
import { CreateNodePage } from '../pages/CreateNodePage';
import { MyInvitationsPage } from '../pages/MyInvitationsPage';
import { PowerupsPage } from '../pages/PowerupsPage';
import { ShowcasePage } from '../pages/ShowcasePage';
import { FeaturesPage } from '../pages/FeaturesPage';
import { ComparisonPage } from '../pages/ComparisonPage';
import { BoardMemoPage } from '../pages/BoardMemoPage';

import { NewsletterStudioPage } from '../pages/NewsletterStudioPage';
import { MembersPage } from '../pages/MembersPage';
import { ToolsDirectoryPage } from '../pages/tools/ToolsDirectoryPage';
import { ToolContainerPage } from '../pages/tools/ToolContainerPage';
import { ToolHubPage } from '../pages/tools/ToolHubPage';
import { ToolboxDirectoryPage } from '../pages/dashboard-toolbox/ToolboxDirectoryPage';
import { ToolboxContainerPage } from '../pages/dashboard-toolbox/ToolboxContainerPage';
import { DemoPage } from '../pages/DemoPage';
import { JoinPage } from '../pages/JoinPage';


import { useCurrentUser } from '../contexts/AuthContext';
import { TIER_GATED_FEATURES } from '../constants';

/** Shows a loading spinner while auth is still initializing, then 404 once resolved. */
const AuthAwareFallback = () => {
  const { isLoading } = useCurrentUser();
  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center min-h-screen animate-in fade-in duration-500">
        <div className="w-8 h-8 border-4 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin" />
        <p className="mt-4 text-sm font-medium text-gray-500 animate-pulse">Loading...</p>
      </div>
    );
  }
  return <NotFoundPage />;
};

/** Dynamic prefix redirect: stores active_org and maps /orgs/:slug/* into internal app routes */
const OrgRouteRedirect = () => {
  const { slug, '*': subPath } = useParams();
  if (slug) {
    localStorage.setItem('active_org', slug);
  }
  const target = subPath ? `/${subPath}?org=${slug}` : `/dashboard?org=${slug}`;
  return <Navigate to={target} replace />;
};

/** Intentionally throws an error to test the ErrorBoundary and notification pipeline. */
const TestCrash = () => {
  throw new Error("This is a deliberate test crash to verify the error reporting pipeline (Slack/Mailgun).");
  return null;
};

export const AppRouter = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/join" element={<JoinPage />} />
      <Route path="/join/:token" element={<JoinPage />} />
      <Route path="/showcase" element={<ShowcasePage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/compare" element={<ComparisonPage />} />
      <Route path="/compare/:slug" element={<ComparisonPage />} />
      <Route path="/plans" element={<PricingPage />} />
      <Route path="/board-memo" element={<BoardMemoPage />} />
      <Route path="/board-pitch" element={<Navigate to="/board-memo" replace />} />
      <Route path="/use-cases" element={<UseCasesPage />} />
      <Route path="/use-cases/:slug" element={<UseCaseDetailPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/mission" element={<MissionPage />} />
      <Route path="/newsroom" element={<NewsroomPage />} />
      <Route path="/newsroom/:slug" element={<NewsroomPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiePage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/dpa" element={<DpaPage />} />
      <Route path="/aup" element={<AupPage />} />
      <Route path="/dmca" element={<DmcaPage />} />
      
      {/* SEO Tools routes */}
      <Route path="/tools/hoa" element={<ToolHubPage hub="hoa" />} />
      <Route path="/tools/union" element={<ToolHubPage hub="union" />} />
      <Route path="/tools/youth-sports" element={<ToolHubPage hub="youth-sports" />} />
      <Route path="/tools/nonprofit" element={<ToolHubPage hub="nonprofit" />} />
      <Route path="/tools/church" element={<ToolHubPage hub="church" />} />
      <Route path="/tools/association" element={<ToolHubPage hub="association" />} />
      <Route path="/tools/greek-life" element={<ToolHubPage hub="greek-life" />} />
      <Route path="/tools/alumni" element={<ToolHubPage hub="alumni" />} />
      <Route path="/tools/guild" element={<ToolHubPage hub="guild" />} />
      <Route path="/tools/advocacy" element={<ToolHubPage hub="advocacy" />} />
      <Route path="/tools/senior-living" element={<ToolHubPage hub="senior-living" />} />
      <Route path="/tools" element={<ToolsDirectoryPage />} />
      <Route path="/tools/:slug" element={<ToolContainerPage />} />
      
      {/* Demo route */}
      <Route path="/demo" element={<DemoPage />} />

      {/* Global Portal routes */}
      <Route path="/global" element={<GlobalPortal />} />
      <Route path="/:slug/pulse" element={<PublicPulsePage />} />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/sso-callback" element={
        <div className="flex items-center justify-center min-h-screen bg-white dark:bg-black">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent flex items-center justify-center animate-spin rounded-full mb-6"></div>
            <p className="font-bold text-navy-900 dark:text-white text-lg">Completing sign in...</p>
          </div>
          <AuthenticateWithRedirectCallback signUpForceRedirectUrl="/dashboard" signInForceRedirectUrl="/dashboard" />
        </div>
      } />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/stripe-mock" element={<StripeMockDashboardPage />} />
      <Route path="/stripe/callback" element={<StripeCallbackPage />} />
      <Route path="/powerups" element={
        <ProtectedRoute>
          <PowerupsPage />
        </ProtectedRoute>
      } />
      <Route path="/toolbox" element={
        <ProtectedRoute>
          <ToolboxDirectoryPage />
        </ProtectedRoute>
      } />
      <Route path="/toolbox/:slug" element={
        <ProtectedRoute>
          <ToolboxContainerPage />
        </ProtectedRoute>
      } />

      {/* Branded node routes */}
      <Route path="/system-admin" element={
        <ProtectedRoute requireSystemAdmin>
          <SystemAdminPage />
        </ProtectedRoute>
      } />
      <Route path="/system-admin/templates" element={
        <ProtectedRoute requireSystemAdmin>
          <TemplatesPage />
        </ProtectedRoute>
      } />
      <Route path="/system-admin/domains" element={<Navigate to="/system-admin" replace />} />
      <Route path="/system-admin/audit" element={
        <ProtectedRoute requireSystemAdmin>
          <AuditPage />
        </ProtectedRoute>
      } />
      <Route path="/system-admin/tickets" element={
        <ProtectedRoute requireSystemAdmin>
          <TicketsPage />
        </ProtectedRoute>
      } />
      <Route path="/system-admin/newsroom" element={
        <ProtectedRoute requireSystemAdmin>
          <NewsroomManagerPage />
        </ProtectedRoute>
      } />
      <Route path="/system-admin/test-crash" element={
        <ProtectedRoute requireSystemAdmin>
          <TestCrash />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/pulse" element={
        <ProtectedRoute requireModule="PULSE">
          <PulsePage />
        </ProtectedRoute>
      } />
      
      <Route path="/newsletter/studio/:id" element={
        <ProtectedRoute requireNode requireModule="NEWSLETTER" requireAction="create_newsletter">
          <NewsletterStudioPage />
        </ProtectedRoute>
      } />
      
      <Route path="/newsletter/studio" element={
        <ProtectedRoute requireNode requireModule="NEWSLETTER" requireAction="create_newsletter">
          <NewsletterStudioPage />
        </ProtectedRoute>
      } />
      
      <Route path="/newsletter" element={
        <ProtectedRoute requireNode requireModule="NEWSLETTER" requireAction="view_newsletter">
          <NewsletterPage />
        </ProtectedRoute>
      } />
      
      <Route path="/discussions" element={
        <ProtectedRoute requireModule="DISCUSSIONS" requireAction="view_discussions">
          <DiscussionsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/polls" element={
        <ProtectedRoute requireModule="POLLS" requireAction="view_polls">
          <PollsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/chat" element={
        <ProtectedRoute requireModule="CHAT" requireAction="view_chat" requireTier={TIER_GATED_FEATURES.chat}>
          <ChatPage />
        </ProtectedRoute>
      } />
      
      <Route path="/calendar" element={
        <ProtectedRoute requireModule="CALENDAR" requireAction="view_calendar">
          <CalendarPage />
        </ProtectedRoute>
      } />
      
      <Route path="/ledger" element={
        <ProtectedRoute requireModule="LEDGER" requireAction={['view_ledger_admin', 'view_personal_finances']}>
          <LedgerPage />
        </ProtectedRoute>
      } />
      
      <Route path="/requirements" element={
        <ProtectedRoute requireModule="REQUIREMENTS" requireAction="view_requirements">
          <RequirementsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/goals" element={
        <ProtectedRoute requireModule="GOALS" requireAction="view_goals">
          <GoalsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/vault" element={
        <ProtectedRoute requireModule="VAULT" requireAction="manage_vault">
          <VaultPage />
        </ProtectedRoute>
      } />
      
      <Route path="/sub-orgs" element={
        <ProtectedRoute requireAction="view_suborgs" requireTier={TIER_GATED_FEATURES.subOrgs}>
          <SubOrgsPage />
        </ProtectedRoute>
      } />
      
      {/* Retired: the tree view is now a toggle inside SubOrgsPage (List / Structure).
          Redirects so existing bookmarks and the old sidebar entry keep working. */}
      <Route path="/hierarchy" element={<Navigate to="/sub-orgs" replace />} />
      
      <Route path="/referrals" element={
        <ProtectedRoute requireAction="manage_referrals">
          <ReferralsPage />
        </ProtectedRoute>
      } />

      <Route path="/leadership" element={
        <ProtectedRoute requireAction="view_leadership">
          <LeadershipPage />
        </ProtectedRoute>
      } />

      <Route path="/elections" element={
        <ProtectedRoute requireAction="view_elections">
          <ElectionsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      
      <Route path="/create-org" element={
        <ProtectedRoute>
          <CreateNodePage />
        </ProtectedRoute>
      } />

      <Route path="/invitations" element={
        <ProtectedRoute>
          <MyInvitationsPage />
        </ProtectedRoute>
      } />

      <Route path="/profile/notifications" element={
        <ProtectedRoute>
          <NotificationSettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/members" element={
        <ProtectedRoute requireAction="manage_members">
          <MembersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/directory" element={
        <ProtectedRoute requireModule="DIRECTORY" requireAction="view_directory">
          <DirectoryPage />
        </ProtectedRoute>
      } />
      
      <Route path="/support" element={
        <ProtectedRoute requireModule="SUPPORT">
          <SupportPage />
        </ProtectedRoute>
      } />
      
      <Route path="/analytics" element={
        <ProtectedRoute requireAction="view_analytics">
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute requireAction="edit_settings">
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/modules" element={
        <ProtectedRoute requireAction="edit_settings">
          <ModuleSettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/branding" element={
        <ProtectedRoute requireAction="edit_settings">
          <BrandingPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/terminology" element={
        <ProtectedRoute requireAction="edit_settings">
          <TerminologyPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/members" element={
        <ProtectedRoute requireAction="edit_settings">
          <MemberSettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/billing" element={
        <ProtectedRoute requireAction="edit_settings">
          <BillingSettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/payouts" element={
        <ProtectedRoute requireAction="edit_settings">
          <PayoutSettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings/data" element={
        <ProtectedRoute requireAction="edit_settings">
          <DataSettingsPage />
        </ProtectedRoute>
      } />

      {/* Dynamic Org Prefix Redirects */}
      <Route path="/orgs/:slug/*" element={<OrgRouteRedirect />} />
      <Route path="/orgs/:slug" element={<OrgRouteRedirect />} />

      {/* Fallback */}
      <Route path="*" element={<AuthAwareFallback />} />
    </Routes>
  );
};

